import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router';
import styles from './App.module.scss';
import { Navbar } from './components/Navbar/Navbar';

import axios from 'axios';
import { connect } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Dispatch } from 'redux';
import { setUpAxios } from './api/utils';
import { VerificationError } from './components/VerificationError/VerificationError';
import { VerificationSuccess } from './components/VerificationSuccess/VerificationSuccess';
import { ConnectedCustomRoute } from './components/CustomRoute/CustomRoute';
import { ConnectedPrivateTipsterRoute } from './components/CustomRoute/PrivateTipsterRoute';
import { SalesConnected } from './components/Dashboard/Sales';
import { StatsConnected } from './components/Dashboard/Stats';
import { LoginConnected } from './components/Login/Login';
import { MyTipsConnected } from './components/MyTips/MyTips';
import { RecoverPasswordConnected } from './components/RecoverPassword/RecoverPassword';
import { ResetPasswordConnected } from './components/ResetPassword/ResetPassword';
import { VerifyConnected } from './components/Verify/Verify';

import ScrollToTop from './ScrollToTop';
import { RootState } from './store';
import { loginActions } from './store/login/actions';
import { userSelector } from './store/login/selector';

import { BounceLoader } from 'react-spinners';
import { useCookies } from 'react-cookie';
import { CookiesPopup } from './components/CookiesPopup/CookiesPopup';

import { LoginState } from './store/login/reducer';
import { getVisitorId } from './utils/getVisitorId';
import { AutoLoginConnected } from './components/Redirects/Autologin';
import { TipPublishFormConnected } from './components/TipPublish/TipPublishForm';
import { TipPublishPopupConnected } from './components/TipPublish/TipPublishPopup/TipPublishPopup';
import { IPBasedRedirectConnected } from './components/Redirects/CountryBasedRedirect';

//stripe
import { StripeProvider } from 'react-stripe-elements';
import { STRIPE_KEY } from './constants/payments';
//tr
import { useTranslation } from 'react-i18next';
import { tr } from './utils/translations/translator';
import {
  ITipsterRoute,
  createTipsterRoutes,
  getTipsterRoutes,
} from './components/Redirects/DynamicRoutes';
import { ErrorInfo } from './utils/errorHandling/ErrorInfo';
import { SignupConnected } from './components/Signup/Signup';
import { createCustomTipsterLinks } from './components/Redirects/CustomTipsterLinks';
import { ShopContainerConnected } from './components/Shops/ShopContainer';
import { settingsActions } from './store/settings/actions';
import { SettingsState } from './store/settings/reducer';
import { StoreTypesReversed } from './components/PaymentGateway/interfaces/store_types';
import { useAnalyticsHandlers } from './analytics/__providers__/AnalyticsHandlersProvider';
import { useFeatureFlagContext } from './components/__providers__/FeatureFlagsContext';
import { OldTipPublishPopupConnected } from './components/TipPublish/TipPublishPopup/OldTipPublishPopup';
import { BalanceConnected } from './components/Dashboard/Balance';
import { ShopRedirect } from './components/Redirects/ShopRedirect';
import { PaymentStageProvider } from './components/PaymentGateway/__providers__/PaymentStageContext';
import { IPFetcherContextProvider } from './components/Shops/shared/__providers__/IPFetcherProvider';
import { TipsterRoutesProvider } from './components/PaymentGateway/__providers__/TipsterRoutesProvider';
import { TipsterFeeTiersProvider } from './components/__providers__/TipsterFeeTiersProvider';
import { Affiliation } from './components/Affiliation/Affiliation';

interface DispatchProps {
  requestTipsters: () => void;
  requestLogout: () => void;
  loginInit: () => void;
  requestSettings: () => void;
}

interface StateProps {
  user: any;
  loginState: LoginState;
  settingsState: SettingsState;
}

interface OwnProps {}

type AppProps = OwnProps & StateProps & DispatchProps & RouteComponentProps;

const loadScript = (src: any) =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.addEventListener('load', () => {
      resolve({ successful: true });
    });
    script.addEventListener('error', (event) => {
      reject({ error: event });
    });
    document.head.appendChild(script);
  });

export const App: React.FC<AppProps> = ({
  loginState,
  user,
  settingsState,
  requestSettings,

  ...props
}) => {
  const { t } = useTranslation();
  const analytics = useAnalyticsHandlers();
  const [stripeLoaded, setStripeLoaded] = useState({ successful: false });
  const [cookies, setCookie] = useCookies(['visitor_id', 'cookies_accepted']);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [tipsterRoutes, setTipsterRoutes] = useState<
    ITipsterRoute[] | undefined
  >(undefined);

  const { setFeatureFlags, isEnabled } = useFeatureFlagContext();

  const now = new Date();
  const expires = new Date(
    now.getFullYear() + 1,
    now.getMonth(),
    now.getDate(),
  );

  useEffect(() => {
    props.loginInit();
    const fetchData = async () => {
      const result: any = await loadScript('https://js.stripe.com/v3/');
      setStripeLoaded(result);
    };
    fetchData();
    const fetchRoutes = async () => {
      const tipsterRoutes = await getTipsterRoutes();
      setTipsterRoutes(tipsterRoutes);
    };
    fetchRoutes();
    requestSettings();

    return () => {
      analytics?.client.Mixpanel.reset();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const visitorID = cookies['visitor_id'];
    const areCookiesAccepted = Boolean(cookies['cookies_accepted']);

    if (
      areCookiesAccepted &&
      loginState.requested &&
      !loginState.isLoading &&
      !user &&
      !visitorID
    ) {
      getVisitorId().then((visitorID: any) => {
        setCookie('visitor_id', visitorID.id, { path: '/', expires });
        if (!sessionStarted) {
          analytics!.client.BIEvent.log('SESSION_START', {
            values: [null],
            userID: visitorID.id,
          });
          setSessionStarted(true);
        }
      });
    } else if (
      loginState.requested &&
      !loginState.isLoading &&
      user &&
      !sessionStarted
    ) {
      analytics!.client.BIEvent.log('SESSION_START', {
        values: [null],
        userID: user.id,
      });
      setSessionStarted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState]);

  const setAuthToken = get(axios, [
    'defaults',
    'headers',
    'common',
    'Authorization',
  ]);
  const areSettingsLoaded = useMemo(
    () =>
      settingsState.requested && !settingsState.isLoading && settingsState.data,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingsState.data],
  );
  useEffect(() => {
    if (settingsState && settingsState.data) {
      setFeatureFlags(settingsState.data.feature_flags);
    }
  }, [settingsState]);

  !setAuthToken && setUpAxios();

  //TODO: delete after flag permanently enabled
  const isChannelsFlagEnabled = isEnabled('channel_based_tips_publishing');

  // const [canAccessAffiliation, setCanAccessAffiliation] = useState(false);
  // const [isLoading, setIsLoading] = useState(true);

  // useEffect(() => {
  //   const fetchAffiliationAccess = async () => {
  //     if (user && user.id) {
  //       try {
  //         const response = await axios.get(`https://api.redirect.tipsterchat.com/api/tipsters/${user.id}/can-access-affiliation`, {
  //           headers: {
  //             Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhdWQiOiJ0aXBzdGVyY2hhdCIsImV4cCI6MTcxNjgyMTc0NywiaWF0IjoxNzE2ODIwODQ3LCJpc3MiOiJ0aXBzdGVyY2hhdC5jb20iLCJzdWIiOiJjcDg5N2owcGZxcWlicm1lYWc0ZyJ9.ZI4-n0iPeKliwXIbAbttQMPvRf7np4e10sFe_K7Tb88`
  //           }
  //         });
  //         setCanAccessAffiliation(response.data.can_access);
  //       } catch (error) {
  //         console.error('Error fetching affiliation access:', error);
  //       } finally {
  //         setIsLoading(false);
  //       }
  //     }
  //   };
  //   fetchAffiliationAccess();
  // }, [user]);

  return (
    <>
      <div className={get(user, 'is_tipster') ? styles.flex : styles.void}>
        <Navbar
          user={user}
          isTipster={get(user, 'is_tipster')}
          userImg={get(user, ['avatar_url'])}
          requestLogout={props.requestLogout}
        />
        <ScrollToTop />
        <div className={styles.content}>
          {stripeLoaded &&
          stripeLoaded.successful &&
          tipsterRoutes &&
          areSettingsLoaded ? (
            <StripeProvider apiKey={STRIPE_KEY}>
              <PaymentStageProvider>
                <IPFetcherContextProvider>
                  <TipsterRoutesProvider tipsterRoutes={tipsterRoutes}>
                    <Switch>
                      {createTipsterRoutes(tipsterRoutes)}
                      {createCustomTipsterLinks()}
                      <Route
                        path={'/shop/:productId'}
                        component={ShopRedirect}
                        exact
                      />
                      <Route
                        path={'/verifications'}
                        component={VerifyConnected}
                      />
                      <Route
                        path={'/reset-password'}
                        component={ResetPasswordConnected}
                        exact
                      />
                      <Route
                        path={'/recover-password'}
                        component={RecoverPasswordConnected}
                        exact
                      />
                      <Route
                        path={'/verification-success'}
                        component={VerificationSuccess}
                      />
                      <Route
                        path={'/verification-error'}
                        component={VerificationError}
                      />
                      <Route path={'/errorInfo'} component={ErrorInfo} exact />
                      <Route
                        component={AutoLoginConnected}
                        path={'/auth/:tokenInfo'}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/retousopenx10'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/reto_blackfriday'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/acceder-reto-navidad'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/acceso-gratis-retoatpfinals-es'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/acceso-gratis-retoatpfinals-mx'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/grupomiami'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/premiummallicwinners'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/grupobarcelona'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/captura'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/premium_batallamadrid'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/lasvegas'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={() => (
                          <ShopContainerConnected
                            storeType={StoreTypesReversed.TipPurchase}
                          />
                        )}
                        path={'/tips/:id'}
                        isPublic
                      />
                      <ConnectedCustomRoute
                        path={'/login'}
                        component={LoginConnected}
                        isPublic
                      />
                      <ConnectedCustomRoute
                        path={'/signup'}
                        component={SignupConnected}
                        isPublic
                      />
                      <ConnectedCustomRoute
                        path={'/login/:redirect'}
                        component={LoginConnected}
                        isPublic
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/user-stats'}
                        component={StatsConnected}
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/my-bets'}
                        component={MyTipsConnected}
                      />
                        <ConnectedPrivateTipsterRoute
                          component={(props: any) => (
                            <Affiliation user={user} {...props} />
                          )}
                          path={'/affiliation'}
                        />

                      <ConnectedPrivateTipsterRoute
                        path={'/balance'}
                        component={BalanceConnected}
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/payouts'}
                        component={BalanceConnected}
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/betPublish/simple'}
                        component={TipPublishFormConnected}
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/betPublish/combined'}
                        component={TipPublishFormConnected}
                      />

                      <ConnectedPrivateTipsterRoute
                        path={'/pc'}
                        component={TipPublishPopupConnected}
                      />

                      <TipsterFeeTiersProvider>
                        <ConnectedPrivateTipsterRoute
                          path={'/sales'}
                          component={SalesConnected}
                        />

                        <ConnectedPrivateTipsterRoute
                          path={'/earnings'}
                          component={SalesConnected}
                        />
                      </TipsterFeeTiersProvider>
                      <Redirect to={`/my-bets`} exact from={'/'} />
                    </Switch>
                  </TipsterRoutesProvider>
                </IPFetcherContextProvider>
              </PaymentStageProvider>
            </StripeProvider>
          ) : (
            <div className={styles.loader}>
              <BounceLoader color={'#27de81'} size={25} />
              <p>{tr(t, 'loading')} ...</p>
            </div>
          )}
        </div>
      </div>
      <CookiesPopup />
      <ToastContainer />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  user: userSelector(state),
  loginState: state.login,
  settingsState: state.settings,
});
const mapDispatchToProps = (dispatch: Dispatch) => ({
  loginInit: () => {
    dispatch(loginActions.loginInitAction());
  },
  requestLogout: () => {
    dispatch(loginActions.logoutRequestAction());
  },
  requestSettings: () => {
    dispatch(settingsActions.settingsRequestAction());
  },
});

export const ConnectedApp = withRouter(
  connect<StateProps, OwnProps, DispatchProps, RootState>(
    mapStateToProps,
    mapDispatchToProps,
  )(App),
);
